// Update your src/components/HeatDistribution.js file
import React, { useState } from 'react';
import './HeatDistribution.css';
import PrintableHeatMatrix from './PrintableHeatMatrix';
import { useCompetition } from '../competition/CompetitionContext';

//const _host = 'chappie-demo.novosense.africa';
const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const HeatDistribution = ({ event, heats, onRegenerateHeats }) => {
  const [selectedDance, setSelectedDance] = useState(heats && heats.length > 0 ? 0 : null);
  const [showPrintable, setShowPrintable] = useState(false);

  const { selectedCompetition } = useCompetition();

  if (!event) {
    return (
      <div className="heat-distribution-empty">
        <p>Loading event data...</p>
      </div>
    );
  }

  if (!heats || !Array.isArray(heats)) {
    return (
      <div className="heat-distribution-empty">
        <p>No heat data available.</p>
        <button
          className="regenerate-heats-btn"
          onClick={onRegenerateHeats}
        >
          Generate Heats
        </button>
      </div>
    );
  }

  if (!event.dancers || event.dancers.length === 0) {
    return (
      <div className="heat-distribution-empty">
        <p>No dancers have been registered for this event yet.</p>
        <p>Please add dancers in the "Dancers" tab to generate heat distributions.</p>
        <button
          className="regenerate-heats-btn"
          onClick={onRegenerateHeats}
        >
          Generate Heats
        </button>
      </div>
    );
  }

  if (heats.length === 0) {
    return (
      <div className="heat-distribution-empty">
        <p>No heats have been generated yet.</p>
        <button
          className="regenerate-heats-btn"
          onClick={onRegenerateHeats}
        >
          Generate Heats
        </button>
      </div>
    );
  }

  const handleDanceSelect = (index) => {
    setSelectedDance(index);
  };

  const handleCleanPDFDownload = async () => {
    try {
      console.log('🔄 Fetching heat distribution data from API...');
      console.log('🔍 Event data:', event);
      console.log('🔍 Event ID:', event.id);

      if (!event || !event.id) {
        throw new Error('Event data is missing or incomplete');
      }

      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}/heat-distribution-pdf`);

      console.log('🔍 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error(`Failed to fetch heat distribution data: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Received heat distribution data:', data);

      if (!data || !data.summary || !data.heatMatrix) {
        throw new Error('Invalid heat distribution data received from API');
      }

      const printWindow = window.open('', '_blank');

      if (!printWindow) {
        alert('Please allow popups for this website to enable PDF generation.');
        return;
      }

      const headerRow = data.heatMatrix[0] || [];
      const danceNames = headerRow.slice(1, -1);
      const dancerRows = data.heatMatrix.slice(1) || [];

      const cleanHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${data.summary.eventName || 'Event'} - Heat Distribution</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }

              body {
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                font-size: 17px;
                color: #333;
                line-height: 1.4;
                padding: 20px;
              }

              .print-header {
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 2px solid #3f51b5;
                padding-bottom: 20px;
              }

              .print-header h1 {
                color: #3f51b5;
                font-size: 24px;
                margin-bottom: 10px;
              }

              .print-header h2 {
                color: #555;
                font-size: 18px;
                margin-bottom: 15px;
              }

              .event-details {
                color: #666;
                font-size: 14px;
                margin-bottom: 10px;
              }

              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 30px;
                font-size: 17px;
              }

              th {
                background-color: #f5f5f5;
                font-weight: 600;
                color: #333;
                text-align: center;
                padding: 8px;
                border: 1px solid #ddd;
              }

              td {
                padding: 8px;
                border: 1px solid #ddd;
                text-align: center;
                vertical-align: middle;
              }

              .dancer-cell {
                text-align: center;
                min-width: 150px;
                font-weight: 600;
              }

              .dancer-name {
                font-size: 11px;
                color: #555;
              }

              .in-heat {
                background-color: #e8eaf6;
                color: #3f51b5;
                font-weight: 500;
              }

              .print-footer {
                text-align: center;
                margin-top: 40px;
                padding-top: 20px;
                border-top: 1px solid #ddd;
                color: #666;
                font-size: 11px;
              }

              @media print {
                body { margin: 0.5in; }
                .print-header { page-break-after: avoid; }
                table { page-break-inside: avoid; }
                tr { page-break-inside: avoid; }
              }

              @page {
                margin: 0.5in;
                size: portrait;
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>${data.summary.eventName || 'Event'}</h1>
              <h2>${data.summary.roundType || 'Competition'} - Heat Distribution</h2>
              <div class="event-details">
                <p><strong>Dances:</strong> ${(data.summary.dances || []).join(', ') || 'N/A'}</p>
                <p><strong>Number of Heats:</strong> ${data.summary.numHeats || 'N/A'}</p>
                <p><strong>Total Dancers:</strong> ${data.summary.totalDancers || 0}</p>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Cloth Number</th>
                  ${danceNames.map(dance => `<th>${dance}</th>`).join('')}
                  <th>Total Heats</th>
                </tr>
              </thead>
              <tbody>
                ${dancerRows.map(row => {
                  const dancerInfo = row[0] || '';
                  const heatAssignments = row.slice(1, -1) || [];
                  const totalHeats = row[row.length - 1] || '0';

                  const clothNumber = dancerInfo.split('\n')[0] || dancerInfo.split(' ')[0] || dancerInfo;

                  return `
                    <tr>
                      <td class="dancer-cell">
                        <strong>${clothNumber}</strong>
                      </td>
                      ${heatAssignments.map(heat =>
                        `<td class="${heat !== '-' ? 'in-heat' : ''}">${heat}</td>`
                      ).join('')}
                      <td><strong>${totalHeats}</strong></td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>

            <div class="print-footer">
              <p>Printed on: ${new Date().toLocaleDateString()} - Generated by Flokraft Ultimate System</p>
            </div>
          </body>
        </html>
      `;

      console.log('📄 Generated HTML length:', cleanHTML.length);

      printWindow.document.write(cleanHTML);
      printWindow.document.close();

      console.log('🖨️ Opening print dialog...');

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
          };
          setTimeout(() => {
            if (!printWindow.close) {
              printWindow.close();
            }
          }, 2000);
        }, 500);
      };

    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      alert(`Failed to generate PDF: ${error.message}`);
    }
  };

  const handleAdjudicatorCards = async () => {
    try {
      if (!heats || heats.length === 0) {
        alert('No heat data available for adjudicator cards');
        return;
      }

      const isFinal = event.roundType?.toUpperCase() === 'FINAL';
      const eventName = event.name || 'Event';
      const competitionName = selectedCompetition?.name || 'Competition';

      console.log('🎯 Generating Adjudicator Cards for all dances in:', { eventName, isFinal, totalDances: heats.length });

      const cardsHTML = generateAllDanceAdjudicatorCardsHTML(eventName, competitionName, heats, isFinal);

      const printWindow = window.open('', '_blank');

      if (!printWindow) {
        alert('Please allow popups for this website to enable adjudicator cards generation.');
        return;
      }

      printWindow.document.write(cardsHTML);
      printWindow.document.close();

      console.log('🃏 Opening print dialog for Adjudicator Cards...');

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
          };
          setTimeout(() => {
            if (!printWindow.close) {
              printWindow.close();
            }
          }, 2000);
        }, 500);
      };

    } catch (error) {
      console.error('❌ Error generating Adjudicator Cards:', error);
      alert(`Failed to generate Adjudicator Cards: ${error.message}`);
    }
  };

  const generateAllDanceAdjudicatorCardsHTML = (eventName, competitionName, allDances, isFinal) => {
  const currentDate = new Date().toLocaleDateString();

  if (isFinal) {
    // Keep the final round cards as they are
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${eventName} - All Dances - Adjudicator Cards</title>
          <style>
            @page {
              margin: 0.5in;
              size: landscape;
            }

            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              font-size: 10px;
              color: #333;
              padding: 10px;
              background: white;
            }

            .page-container {
              width: 100%;
              height: 100vh;
              display: flex;
              flex-direction: column;
            }

            .cards-container {
              display: flex;
              gap: 30px;
              flex: 1;
              padding: 15px;
            }

            .adjudicator-card {
              flex: 1;
              border: 2px solid #000;
              display: flex;
              flex-direction: column;
              background: white;
            }

            .card-header {
              text-align: center;
              padding: 15px 10px 10px;
              border-bottom: 2px solid #000;
            }

            .card-header h2 {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
              line-height: 1.2;
            }

            .card-header .competition-name {
              font-size: 12px;
              color: #333;
              font-weight: bold;
              margin-bottom: 5px;
            }

            .card-header h3 {
              font-size: 14px;
              color: #666;
              margin-bottom: 5px;
              font-weight: normal;
            }

            .card-header .dance-name {
              font-size: 20px;
              color: #000;
              font-weight: bold;
              margin-bottom: 5px;
            }

            .dancers-table {
              width: 100%;
              border-collapse: collapse;
              flex: 1;
            }

            .dancers-table th,
            .dancers-table td {
              border: 1px solid #000;
              text-align: center;
              font-size: 10px;
              vertical-align: middle;
            }

            .dancers-table th {
              background-color: #f0f0f0;
              font-weight: bold;
              height: 45px;
              font-size: 12px;
            }

            .dancers-table .place-cell {
              width: 25%;
              height: 55px;
            }

            .dancers-table .cloth-cell {
              width: 25%;
              height: 55px;
              font-weight: bold;
              font-size: 28px;
              color: #000;
            }

            .card-footer {
              text-align: center;
              padding: 12px 10px;
              border-top: 1px solid #ccc;
              font-size: 10px;
              color: #666;
              background: #f9f9f9;
            }

            .card-footer div {
              margin-bottom: 3px;
            }

            .card-footer div:last-child {
              margin-bottom: 0;
            }

            @media print {
              body { 
                margin: 0;
                padding: 0;
              }
              .page-container {
                height: 100vh;
              }
              .cards-container {
                padding: 10px;
                gap: 20px;
              }
              .adjudicator-card {
                break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${allDances.map((dance, index) => {
            const allDancers = [];

            dance.heats.forEach(heat => {
              heat.dancers.forEach(dancer => {
                allDancers.push({
                  clothNumber: dancer.clothNumber || dancer.cloth_number,
                  name: dancer.name || dancer.lead_name || '',
                  heatNumber: heat.heatNumber
                });
              });
            });

            allDancers.sort((a, b) => {
              const aNum = parseInt(a.clothNumber);
              const bNum = parseInt(b.clothNumber);
              return aNum - bNum;
            });

            return `
              <div class="page-container">
                <div class="cards-container">
                  <!-- First Adjudicator Card -->
                  <div class="adjudicator-card">
                    <div class="card-header">
                      <h2>${eventName}</h2>
                      <div class="competition-name">${competitionName}</div>
                      <h3>FINAL ROUND</h3>
                      <div class="dance-name">${dance.dance}</div>
                    </div>

                    <table class="dancers-table">
                      <thead>
                        <tr>
                          <th class="cloth-cell">Cloth Number</th>
                          <th class="place-cell">Placing</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${allDancers.slice(0, 8).map((dancer) => `
                          <tr>
                            <td class="cloth-cell">${dancer.clothNumber}</td>
                            <td class="place-cell"></td>
                          </tr>
                        `).join('')}
                        ${Array(Math.max(0, 8 - allDancers.length)).fill(0).map(() => `
                          <tr>
                            <td class="cloth-cell"></td>
                            <td class="place-cell"></td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>

                    <div class="card-footer">
                      <div>Adjudicator: _________________________</div>
                      <div>Date: ${currentDate}</div>
                    </div>
                  </div>

                  <!-- Second Adjudicator Card -->
                  <div class="adjudicator-card">
                    <div class="card-header">
                      <h2>${eventName}</h2>
                      <div class="competition-name">${competitionName}</div>
                      <h3>FINAL ROUND</h3>
                      <div class="dance-name">${dance.dance}</div>
                    </div>

                    <table class="dancers-table">
                      <thead>
                        <tr>
                          <th class="cloth-cell">Cloth Number</th>
                          <th class="place-cell">Placing</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${allDancers.slice(0, 8).map((dancer) => `
                          <tr>
                            <td class="cloth-cell">${dancer.clothNumber}</td>
                            <td class="place-cell"></td>
                          </tr>
                        `).join('')}
                        ${Array(Math.max(0, 8 - allDancers.length)).fill(0).map(() => `
                          <tr>
                            <td class="cloth-cell"></td>
                            <td class="place-cell"></td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>

                    <div class="card-footer">
                      <div>Adjudicator: _________________________</div>
                      <div>Date: ${currentDate}</div>
                    </div>
                  </div>
                </div>
              </div>
            `;
          }).join('')}
          <div class="print-footer" style="position: fixed; bottom: 5px; left: 0; right: 0; text-align: center; font-size: 7px; color: #808080;">
            Generated by Flokraft Ultimate DanceSport Management System © 2025
          </div>
        </body>
      </html>
    `;
  } else {
    // Modified callback cards with two identical cards side by side on landscape page
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${eventName} - All Dances - Adjudicator Cards</title>
          <style>
            @page {
              margin: 0.5in;
              size: landscape;
            }

            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              font-size: 12px;
              color: #333;
              padding: 15px;
              background: white;
            }

            .page-container {
              width: 100%;
              min-height: 100vh;
              display: flex;
              flex-direction: column;
              page-break-after: always;
            }

            .page-container:last-child {
              page-break-after: auto;
            }

            .cards-container {
              display: flex;
              gap: 20px;
              justify-content: space-between;
              flex: 1;
              padding: 10px 0;
            }

            .adjudicator-card {
              width: calc(50% - 10px);
              border: 2px solid #000;
              display: flex;
              flex-direction: column;
              background: white;
              break-inside: avoid;
            }

            .card-header {
              text-align: center;
              padding: 15px 10px;
              border-bottom: 2px solid #000;
            }

            .card-header h1 {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 5px;
              line-height: 1.2;
            }

            .card-header h2 {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
              color: #000;
            }

            .card-header h3 {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 5px;
              color: #333;
            }

            .cloth-numbers-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              padding: 20px;
              justify-items: center;
              flex: 1;
            }

            .cloth-number {
              width: 60px;
              height: 60px;
              border: 2px solid #000;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 20px;
              font-weight: bold;
              background-color: white;
            }

            .card-footer {
              display: flex;
              justify-content: space-between;
              padding: 15px;
              border-top: 1px solid #000;
              font-size: 12px;
            }

            .signature-line {
              flex: 1;
              border-bottom: 1px solid #000;
              margin: 0 10px 5px;
              height: 20px;
            }

            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              .page-container {
                height: 100vh;
                page-break-after: always;
              }
              .page-container:last-child {
                page-break-after: auto;
              }
              .cards-container {
                gap: 15px;
                padding: 5px 0;
              }
              .adjudicator-card {
                width: calc(50% - 8px);
              }
              .cloth-number {
                width: 50px;
                height: 50px;
                font-size: 18px;
              }
            }
          </style>
        </head>
        <body>
          ${allDances.map((dance) => {
            const allDancers = [];

            dance.heats.forEach(heat => {
              heat.dancers.forEach(dancer => {
                allDancers.push({
                  clothNumber: dancer.clothNumber || dancer.cloth_number,
                  name: dancer.name || dancer.lead_name || '',
                  heatNumber: heat.heatNumber
                });
              });
            });

            allDancers.sort((a, b) => {
              const aNum = parseInt(a.clothNumber);
              const bNum = parseInt(b.clothNumber);
              return aNum - bNum;
            });

            // Group dancers by heat number
            const dancersByHeat = {};
            allDancers.forEach(dancer => {
              if (!dancersByHeat[dancer.heatNumber]) {
                dancersByHeat[dancer.heatNumber] = [];
              }
              dancersByHeat[dancer.heatNumber].push(dancer.clothNumber);
            });

            // Sort cloth numbers within each heat
            Object.keys(dancersByHeat).forEach(heatNumber => {
              dancersByHeat[heatNumber].sort((a, b) => parseInt(a) - parseInt(b));
            });

            // Create a page with two identical cards for each heat
            return Object.entries(dancersByHeat).map(([heatNumber, clothNumbers]) => `
              <div class="page-container">
                <div class="cards-container">
                  <!-- First Card -->
                  <div class="adjudicator-card">
                    <div class="card-header">
                      <h1>${competitionName}</h1>
                      <h2>${eventName}</h2>
                      <h3>${dance.dance} - Heat ${heatNumber}</h3>
                    </div>

                    <div class="cloth-numbers-grid">
                      ${clothNumbers.map((clothNumber) => `
                        <div class="cloth-number">${clothNumber}</div>
                      `).join('')}
                    </div>

                    <div class="card-footer">
                      <div>
                        <div>Adjudicator</div>
                        <div class="signature-line"></div>
                      </div>
                      <div>
                        <div>Date</div>
                        <div class="signature-line">${currentDate}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Second Card (Identical) -->
                  <div class="adjudicator-card">
                    <div class="card-header">
                      <h1>${competitionName}</h1>
                      <h2>${eventName}</h2>
                      <h3>${dance.dance} - Heat ${heatNumber}</h3>
                    </div>

                    <div class="cloth-numbers-grid">
                      ${clothNumbers.map((clothNumber) => `
                        <div class="cloth-number">${clothNumber}</div>
                      `).join('')}
                    </div>

                    <div class="card-footer">
                      <div>
                        <div>Adjudicator</div>
                        <div class="signature-line"></div>
                      </div>
                      <div>
                        <div>Date</div>
                        <div class="signature-line">${currentDate}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            `).join('');
          }).join('')}
          <div class="print-footer" style="position: fixed; bottom: 5px; left: 0; right: 0; text-align: center; font-size: 7px; color: #808080;">
            Generated by Flokraft Ultimate DanceSport Management System © 2025
          </div>
        </body>
      </html>
    `;
  }
};

  // eslint-disable-next-line no-unused-vars
  const generateAdjudicatorCardsHTML = (eventName, danceName, competitionName, heatData, isFinal) => {
    const currentDate = new Date().toLocaleDateString();
    const allDancers = [];

    heatData.forEach(heat => {
      heat.dancers.forEach(dancer => {
        allDancers.push({
          clothNumber: dancer.clothNumber || dancer.cloth_number,
          name: dancer.name || dancer.lead_name || '',
          heatNumber: heat.heatNumber
        });
      });
    });

    allDancers.sort((a, b) => {
      const aNum = parseInt(a.clothNumber);
      const bNum = parseInt(b.clothNumber);
      return aNum - bNum;
    });

    if (isFinal) {
      return `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${eventName} - ${danceName} Finals - Adjudicator Cards</title>
            <style>
              @page {
                margin: 0.5in;
                size: landscape;
              }

              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }

              body {
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                font-size: 10px;
                color: #333;
                padding: 10px;
                background: white;
              }

              .page-container {
                width: 100%;
                height: 100vh;
                display: flex;
                flex-direction: column;
              }

              .cards-container {
                display: flex;
                gap: 30px;
                flex: 1;
                padding: 15px;
              }

              .adjudicator-card {
                flex: 1;
                border: 2px solid #000;
                display: flex;
                flex-direction: column;
                background: white;
              }

              .card-header {
                text-align: center;
                padding: 15px 10px 10px;
                border-bottom: 2px solid #000;
              }

              .card-header h2 {
                font-size: 18px;
                font-weight: bold;
                margin-bottom: 5px;
                line-height: 1.2;
              }

              .card-header .competition-name {
                font-size: 12px;
                color: #333;
                font-weight: bold;
                margin-bottom: 5px;
              }

              .card-header h3 {
                font-size: 14px;
                color: #666;
                margin-bottom: 5px;
                font-weight: normal;
              }

              .card-header .dance-name {
                font-size: 20px;
                color: #000;
                font-weight: bold;
                margin-bottom: 5px;
              }

              .dancers-table {
                width: 100%;
                border-collapse: collapse;
                flex: 1;
              }

              .dancers-table th,
              .dancers-table td {
                border: 1px solid #000;
                text-align: center;
                font-size: 10px;
                vertical-align: middle;
              }

              .dancers-table th {
                background-color: #f0f0f0;
                font-weight: bold;
                height: 45px;
                font-size: 12px;
              }

              .dancers-table .place-cell {
                width: 25%;
                height: 55px;
              }

              .dancers-table .cloth-cell {
                width: 25%;
                height: 55px;
                font-weight: bold;
                font-size: 28px;
                color: #000;
              }

              .card-footer {
                text-align: center;
                padding: 12px 10px;
                border-top: 1px solid #ccc;
                font-size: 10px;
                color: #666;
                background: #f9f9f9;
              }

              .card-footer div {
                margin-bottom: 3px;
              }

              .card-footer div:last-child {
                margin-bottom: 0;
              }

              @media print {
                body { 
                  margin: 0;
                  padding: 0;
                }
                .page-container {
                  height: 100vh;
                }
                .cards-container {
                  padding: 10px;
                  gap: 20px;
                }
                .adjudicator-card {
                  break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="page-container">
              <div class="cards-container">
                <!-- First Adjudicator Card -->
                <div class="adjudicator-card">
                  <div class="card-header">
                    <h2>${eventName} - ${danceName}</h2>
                    <div class="competition-name">${competitionName}</div>
                    <h3>FINAL ROUND</h3>
                    <div class="dance-name">${danceName}</div>
                  </div>

                  <table class="dancers-table">
                    <thead>
                      <tr>
                        <th class="cloth-cell">Cloth Number</th>
                        <th class="place-cell">Placing</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${allDancers.slice(0, 8).map((dancer, index) => `
                        <tr>
                          <td class="cloth-cell">${dancer.clothNumber}</td>
                          <td class="place-cell"></td>
                        </tr>
                      `).join('')}
                      ${Array(Math.max(0, 8 - allDancers.length)).fill(0).map(() => `
                        <tr>
                          <td class="cloth-cell"></td>
                          <td class="place-cell"></td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>

                  <div class="card-footer">
                    <div>Adjudicator: _________________________</div>
                    <div>Date: ${currentDate}</div>
                  </div>
                </div>

                <!-- Second Adjudicator Card -->
                <div class="adjudicator-card">
                  <div class="card-header">
                    <h2>${eventName} - ${danceName}</h2>
                    <div class="competition-name">${competitionName}</div>
                    <h3>FINAL ROUND</h3>
                    <div class="dance-name">${danceName}</div>
                  </div>

                  <table class="dancers-table">
                    <thead>
                      <tr>
                        <th class="cloth-cell">Cloth Number</th>
                        <th class="place-cell">Placing</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${allDancers.slice(0, 8).map((dancer, index) => `
                        <tr>
                          <td class="cloth-cell">${dancer.clothNumber}</td>
                          <td class="place-cell"></td>
                        </tr>
                      `).join('')}
                      ${Array(Math.max(0, 8 - allDancers.length)).fill(0).map(() => `
                        <tr>
                          <td class="cloth-cell"></td>
                          <td class="place-cell"></td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>

                  <div class="card-footer">
                    <div>Adjudicator: _________________________</div>
                    <div>Date: ${currentDate}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="print-footer" style="position: fixed; bottom: 5px; left: 0; right: 0; text-align: center; font-size: 7px; color: #808080;">
              Generated by Flokraft Ultimate DanceSport Management System © 2025
            </div>
          </body>
        </html>
      `;
    } else {
      return `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${eventName} - ${danceName} - Adjudicator Cards</title>
            <style>
              @page {
                margin: 0.5in;
                size: portrait;
              }

              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }

              body {
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                font-size: 11px;
                color: #333;
                padding: 20px;
                background: white;
              }

              .page-container {
                width: 100%;
                min-height: 100vh;
                display: flex;
                flex-direction: column;
              }

              .cards-container {
                display: flex;
                flex-wrap: wrap;
                gap: 20px;
                justify-content: space-between;
                flex: 1;
              }

              .adjudicator-card {
                width: calc(50% - 10px);
                border: 2px solid #000;
                display: flex;
                flex-direction: column;
                background: white;
                margin-bottom: 20px;
                break-inside: avoid;
              }

              .card-header {
                text-align: center;
                padding: 15px 10px 10px;
                border-bottom: 2px solid #000;
              }

              .card-header h2 {
                font-size: 14px;
                font-weight: bold;
                margin-bottom: 5px;
                line-height: 1.2;
              }

              .card-header .competition-name {
                font-size: 11px;
                color: #333;
                font-weight: bold;
                margin-bottom: 5px;
              }

              .card-header h3 {
                font-size: 16px;
                color: #666;
                margin-bottom: 5px;
                font-weight: normal;
              }

              .card-header .dance-name {
                font-size: 14px;
                color: #000;
                font-weight: bold;
                margin-bottom: 5px;
              }

              .heat-info {
                font-size: 16px;
                color: #333;
                font-weight: bold;
                margin-bottom: 10px;
              }

              .instructions {
                text-align: center;
                margin: 15px 0;
                font-size: 12px;
                font-style: italic;
                color: #666;
              }

              .dancers-table {
                width: 100%;
                border-collapse: collapse;
                margin: 10px 0;
                flex: 1;
              }

              .dancers-table th,
              .dancers-table td {
                border: 1px solid #000;
                text-align: center;
                font-size: 10px;
                vertical-align: middle;
              }

              .dancers-table th {
                background-color: #f0f0f0;
                font-weight: bold;
                height: 40px;
                font-size: 12px;
              }

              .dancers-table .cloth-cell {
                width: 25%;
                height: 45px;
                font-weight: bold;
                font-size: 24px;
                color: #000;
              }

              .dancers-table .selection-cell {
                width: 75%;
                height: 45px;
                padding: 5px;
              }

              .selection-circles {
                display: flex;
                justify-content: center;
                gap: 8px;
                flex-wrap: wrap;
              }

              .circle {
                width: 22px;
                height: 22px;
                border: 1px solid #000;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
              }

              .card-footer {
                text-align: center;
                padding: 12px 10px;
                border-top: 1px solid #ccc;
                font-size: 10px;
                color: #666;
                background: #f9f9f9;
              }

              .card-footer div {
                margin-bottom: 3px;
              }

              .card-footer div:last-child {
                margin-bottom: 0;
              }

              @media print {
                body { 
                  margin: 0;
                  padding: 10px;
                }
                .page-container {
                  min-height: 100vh;
                }
                .cards-container {
                  gap: 15px;
                }
                .adjudicator-card {
                  width: calc(50% - 8px);
                  margin-bottom: 15px;
                  break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="page-container">
              <div class="cards-container">
                ${allDancers.map((dancer, index) => `
                  <div class="adjudicator-card">
                    <div class="card-header">
                      <h2>${eventName} - ${danceName}</h2>
                      <div class="competition-name">${competitionName}</div>
                      <h3>ADJUDICATOR'S CALLBACK CARD</h3>
                      <div class="heat-info">Heat ${dancer.heatNumber} • Couple ${dancer.clothNumber}</div>
                    </div>

                    <div class="instructions">
                      Circle the number of couples to be called back to the next round
                    </div>

                    <table class="dancers-table">
                      <thead>
                        <tr>
                          <th class="cloth-cell">No.</th>
                          <th class="selection-cell">Selection</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${allDancers.map((d, i) => `
                          <tr>
                            <td class="cloth-cell">${d.clothNumber}</td>
                            <td class="selection-cell">
                              <div class="selection-circles">
                                ${[1,2,3,4,5,6,7,8,9,10,11,12].map(num => `
                                  <div class="circle">${num}</div>
                                `).join('')}
                              </div>
                            </td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>

                    <div class="card-footer">
                      <div>Adjudicator: _________________________</div>
                      <div>Signature: _________________________</div>
                      <div>Date: ${currentDate}</div>
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
            <div class="print-footer" style="position: fixed; bottom: 5px; left: 0; right: 0; text-align: center; font-size: 7px; color: #808080;">
              Generated by Flokraft Ultimate DanceSport Management System © 2025
            </div>
          </body>
        </html>
      `;
    }
  };

  if (selectedDance === null || selectedDance === undefined || !heats[selectedDance]) {
    return (
      <div className="heat-distribution-empty">
        <p>No dance selected or heat data unavailable.</p>
        <button
          className="regenerate-heats-btn"
          onClick={() => setSelectedDance(0)}
        >
          Select First Dance
        </button>
      </div>
    );
  }

  const danceHeats = heats[selectedDance].heats;
  const danceName = heats[selectedDance].dance;

  return (
    <div className="heat-distribution">
      {showPrintable && (
        <PrintableHeatMatrix
          event={event}
          heats={heats}
          onClose={() => setShowPrintable(false)}
        />
      )}

      <div className="heat-distribution-header">
      <h3>Heat Distribution</h3>
        <div className="heat-actions">
          <button
            className="print-pdf-btn"
            onClick={handleCleanPDFDownload}
          >
            Download PDF
          </button>
          <button
            className="adjudicator-cards-btn"
            onClick={handleAdjudicatorCards}
          >
            Adjudicator Cards
          </button>
          <button
            className="regenerate-heats-btn"
            onClick={onRegenerateHeats}
            disabled={event.active}
          >
            Regenerate Heats
          </button>
        </div>
      </div>

      
      <div className="dance-selector">
        <p>Select a dance to view its heat distribution:</p>
        <div className="dance-buttons">
          {heats.map((dance, index) => (
            <button
              key={index}
              className={`dance-button ${selectedDance === index ? 'selected' : ''}`}
              onClick={() => handleDanceSelect(index)}
            >
              {dance.dance}
            </button>
          ))}
        </div>
      </div>
      
      <div className="selected-dance-info">
        <h4>{danceName} - Heat Distribution</h4>
        
        <div className="heat-cards">
          {danceHeats.map((heat, heatIndex) => (
            <div key={heatIndex} className="heat-card">
              <div className="heat-card-header">
                <h5>Heat {heat.heatNumber}</h5>
                <span className="dancer-count">{heat.dancers.length} Dancers</span>
              </div>
              
              <ul className="heat-dancers">
                {heat.dancers
                  .sort((a, b) => {
                    const aNum = parseInt(a.clothNumber || a.cloth_number);
                    const bNum = parseInt(b.clothNumber || b.cloth_number);
                    return aNum - bNum;
                  })
                  .map((dancer) => {
                    const clothNumber = dancer.clothNumber || dancer.cloth_number;
                    const dancerName = dancer.name || dancer.lead_name || dancer.follow_name || '';
                    return (
                      <li key={clothNumber} className="heat-dancer">
                        <span className="cloth-number">{clothNumber}</span>
                        <span className="dancer-name">{dancerName}</span>
                      </li>
                    );
                  })}
              </ul>
            </div>
          ))}
        </div>
      </div>
      
      <div className="heat-distribution-note">
        <div className="dancer-dance-matrix">
          <table className="matrix-table">
            <thead>
              <tr>
                <th>Dancer</th>
                {heats.map((dance, index) => (
                  <th key={index}>{dance.dance}</th>
                ))}
                <th>Total Heats</th>
              </tr>
            </thead>
            <tbody>
              {(event.dancers || event.dancers_list || [])
                .sort((a, b) => {
                  const aNum = parseInt(a.clothNumber || a.cloth_number);
                  const bNum = parseInt(b.clothNumber || b.cloth_number);
                  return aNum - bNum;
                })
                .map((dancer) => {
                  const dancerClothNumber = dancer.clothNumber || dancer.cloth_number;
                  const dancerName = dancer.name || dancer.lead_name || dancer.follow_name || '';

                  const dancerHeats = heats.map((dance) => {
                    const heatNumber = dance.heats.findIndex(heat =>
                      heat.dancers.some(d => {
                        const heatDancerClothNumber = d.clothNumber || d.cloth_number;
                        return heatDancerClothNumber === dancerClothNumber;
                      })
                    );
                    return heatNumber !== -1 ? `Heat ${heatNumber + 1}` : '-';
                  });

                  const totalHeats = dancerHeats.filter(h => h !== '-').length;

                  return (
                    <tr key={dancerClothNumber}>
                      <td>
                        <strong>{dancerClothNumber}</strong>
                        <br />
                        <small>{dancerName}</small>
                      </td>
                      {dancerHeats.map((heat, index) => (
                        <td key={index} className={heat !== '-' ? 'in-heat' : ''}>
                          {heat}
                        </td>
                      ))}
                      <td>{totalHeats}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HeatDistribution;